import { Zeeguu_API } from "./classDef";
import qs from "qs";

Zeeguu_API.prototype.getBookmarksDueToday = function (count, callback) {
  this._getJSON(`bookmarks_due_today/${count}`, callback);
};

// All Scheduled User Words
Zeeguu_API.prototype.getAllScheduledUserWords = function (isWithTokens, callback) {
  let endpoint = `all_scheduled_user_words`;
  let payload = {
    with_tokens: isWithTokens,
  };
  if (isWithTokens)
    this._post(
      endpoint,
      qs.stringify(payload),
      callback,
      (error) => {
        console.error(error);
      },
      true,
    );
  else this._getJSON(endpoint, callback);
};

Zeeguu_API.prototype.getCountOfAllScheduledUserWords = function (callback, options) {
  this._getJSON(`count_of_all_scheduled_user_words`, callback, options);
};

// User Words recommended to be studied today
Zeeguu_API.prototype.getCountOfUserWordsRecommendedForPractice = function (callback) {
  this._getJSON(`count_of_user_words_recommended_for_practice`, callback);
};

Zeeguu_API.prototype.getCountOfUserWordsRecommendedForPracticePromise = function () {
  return this._getJSONPromise(`count_of_user_words_recommended_for_practice`);
};

Zeeguu_API.prototype.getUserWordsRecommendedForPractice = function (callback) {
  this._getJSON(`user_words_recommended_for_practice`, callback);
};

Zeeguu_API.prototype.getNextWordDueTime = function (callback) {
  this._getJSON(`next_word_due_time`, callback);
};

Zeeguu_API.prototype.getUserWordsNextInLearning = function (callback, options) {
  this._getJSON(`user_words_next_in_learning`, callback, options);
};

// Backward compatibility (deprecated methods)
Zeeguu_API.prototype.getAllScheduledBookmarks = function (isWithTokens, callback) {
  return this.getAllScheduledUserWords(isWithTokens, callback);
};

Zeeguu_API.prototype.getCountOfAllScheduledWords = function (callback) {
  return this.getCountOfAllScheduledUserWords(callback);
};

Zeeguu_API.prototype.getCountOfBookmarksRecommendedForPractice = function (callback) {
  return this.getCountOfUserWordsRecommendedForPractice(callback);
};

Zeeguu_API.prototype.getBookmarksRecommendedForPractice = function (callback) {
  return this.getUserWordsRecommendedForPractice(callback);
};

Zeeguu_API.prototype.getBookmarksNextInLearning = function (callback) {
  return this.getUserWordsNextInLearning(callback);
};

Zeeguu_API.prototype.uploadExerciseFeedback = function (
  user_feedback,
  exercise_source,
  exercise_solving_speed,
  user_word_id,
  exerciseSessionId,
) {
  let payload = {
    outcome: "other_feedback",
    source: exercise_source,
    solving_speed: exercise_solving_speed,
    user_word_id: user_word_id,
    other_feedback: user_feedback,
    session_id: exerciseSessionId,
  };
  this._post(`report_exercise_outcome`, qs.stringify(payload));
};

Zeeguu_API.prototype.uploadExerciseFinalizedData = function (
  exercise_outcome,
  exercise_source,
  exercise_solving_speed,
  user_word_id,
  exerciseSessionId,
  other_feedback,
  wrong_answers,
) {
  let payload = {
    outcome: exercise_outcome,
    source: exercise_source,
    solving_speed: exercise_solving_speed,
    user_word_id: user_word_id,
    other_feedback: other_feedback,
    session_id: exerciseSessionId,
  };
  // What was typed or picked in each wrong attempt, in order
  if (wrong_answers?.length) payload.wrong_answers = JSON.stringify(wrong_answers);
  this._post(`report_exercise_outcome`, qs.stringify(payload));
};

Zeeguu_API.prototype.getPastContexts = function (userWordId) {
  return this._getJSONPromise(`past_contexts/${userWordId}`);
};

Zeeguu_API.prototype.getAlternativeSentences = function (userWordId) {
  return this._getJSONPromise(`alternative_sentences/${userWordId}`);
};

/**
 * Fetch past contexts and AI-generated alternatives for a word,
 * sorted by sentence length, with the current exercise context first.
 * Deduplication is handled server-side by the two endpoints.
 */
Zeeguu_API.prototype.fetchAndSortContexts = async function (exerciseBookmark) {
  const [pastData, altData] = await Promise.all([
    this.getPastContexts(exerciseBookmark.user_word_id).catch(() => null),
    this.getAlternativeSentences(exerciseBookmark.user_word_id).catch(() => null),
  ]);

  const byLength = (a, b) => (a.sentence || a.context || "").length
                            - (b.sentence || b.context || "").length;

  return [
    { isCurrent: true, bookmark: exerciseBookmark },
    ...(pastData?.past_contexts || []).sort(byLength),
    ...(altData?.examples || []).sort(byLength),
  ];
};

// Whether an answer typed for a bookmark, other than the expected one, is
// also right. Resolves to {relation, explanation}; relation is SYNONYM,
// NEAR_SYNONYM_NOT_IDIOMATIC, NOT_SYNONYM, or UNKNOWN when the API could not
// tell in time (the server gives up after 6s; we stop waiting a bit later).
// `signal` lets the caller abandon the request (the exercise ended meanwhile).
async function judgeTypedAnswer(api, endpoint, bookmarkId, typed, signal, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  signal?.addEventListener("abort", () => controller.abort());
  try {
    const response = await fetch(api._appendSessionToUrl(endpoint), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ bookmark_id: bookmarkId, typed }),
      signal: controller.signal,
    });
    if (!response.ok) return { relation: "UNKNOWN", explanation: "" };
    return await response.json();
  } catch (e) {
    return { relation: "UNKNOWN", explanation: "" };
  } finally {
    clearTimeout(timer);
  }
}

// A word typed into a cloze ("tænker" where it expected "synes").
Zeeguu_API.prototype.judgeClozeAlternative = function (bookmarkId, typed, signal, timeoutMs = 8000) {
  return judgeTypedAnswer(this, "judge_cloze_alternative", bookmarkId, typed, signal, timeoutMs);
};

// A translation typed for the word ("consult" where it expected "look up").
Zeeguu_API.prototype.judgeTranslationAlternative = function (bookmarkId, typed, signal, timeoutMs = 8000) {
  return judgeTypedAnswer(this, "judge_translation_alternative", bookmarkId, typed, signal, timeoutMs);
};

Zeeguu_API.prototype.setPreferredBookmark = function (userWordId, bookmarkId) {
  fetch(this._appendSessionToUrl(`set_preferred_bookmark/${userWordId}`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ bookmark_id: bookmarkId }),
  }).catch((error) => {
    console.error("Failed to persist preferred bookmark:", error);
  });
};

Zeeguu_API.prototype.wordsSimilarTo = function (bookmark_id, callback) {
  this._getJSON(`similar_words/${bookmark_id}`, callback);
};

Zeeguu_API.prototype.getConfusionWords = function (lang, original_sentence, callback) {
  let payload = {
    original_sent: original_sentence,
    language: lang,
  };

  return this._post(`/create_confusion_words`, qs.stringify(payload), callback);
};

Zeeguu_API.prototype.annotateClues = function (word_props, og_sent, lang, callback) {
  let payload = {
    word_with_props: JSON.stringify(word_props),
    original_sentence: og_sent,
    language: lang,
  };
  return this._post(`/annotate_clues`, qs.stringify(payload), callback);
};

Zeeguu_API.prototype.getShorterSimilarSentsInArticle = function (articleText, contextBookmark, lang, callback) {
  let payload = {
    article_text: articleText,
    bookmark_context: contextBookmark,
    language: lang,
  };
  return this._post(`/get_shorter_similar_sents_in_article`, qs.stringify(payload), callback);
};

Zeeguu_API.prototype.getSmallerContext = function (contextBookmark, wordBookmark, lang, contextLen, callback) {
  let payload = {
    bookmark_context: contextBookmark,
    bookmark_word: wordBookmark,
    language: lang,
    max_context_len: contextLen,
  };
  return this._post(`/get_smaller_context`, qs.stringify(payload), callback);
};

Zeeguu_API.prototype.reportExerciseIssue = function (
  bookmarkId,
  exerciseSource,
  reason,
  comment,
  contextUsed,
  callback,
  errorCallback
) {
  const payload = JSON.stringify({
    bookmark_id: bookmarkId,
    exercise_source: exerciseSource,
    reason,
    comment: comment || null,
    context_used: contextUsed || null
  });

  fetch(this._appendSessionToUrl("report_exercise_issue"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: payload
  })
  .then(response => response.json().then(data => ({ status: response.status, data })))
  .then(({ status, data }) => {
    if (status >= 200 && status < 300 && data.success) {
      if (callback) callback(data);
    } else {
      if (errorCallback) errorCallback(data);
    }
  })
  .catch(error => {
    console.error("Error reporting exercise issue:", error);
    if (errorCallback) errorCallback({ error: "Network error" });
  });
};
